import React from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Collapse,
} from 'reactstrap';
import { Form } from '@availity/form';
import { Input, Select, Switch } from '../../lib/form-field';
import { FieldArray } from 'formik';
import '@availity/yup';
import formValidation from './formValidation';
import { parseFollowUpDelays, formatFollowUpDelays } from './followUpDelays';

import { aiChannels, aiOutcomes, aiModels } from '../../config/aiAgents';
import * as aiAgentsActions from '../../store/aiAgents/actions';
import { useSelector, useDispatch } from 'react-redux';
import { SubmitButton } from '../Common/Modal/modalActionButtons';
import { useTranslation } from 'react-i18next';

// Derived from the list instead of repeated, so the default cannot become a
// value the select no longer offers.
const DEFAULT_MODEL = aiModels[0].value;

// The error of a whole list, from a .test() on the array. The errors of single
// entries are an array instead, and already sit under their own inputs.
const ListError = ({ error }) => {
  const { t } = useTranslation();
  return typeof error === 'string' ? (
    <small className="font-size-12 text-danger d-block">{t(error)}</small>
  ) : null;
};

const FormAiAgent = (props) => {
  const editingAiAgent = props.aiAgent || {};
  const leadFields = useSelector((state) => state.LeadField.leadFields);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const initialValues = {
    name: '',
    channel: 'whatsapp',
    active: true,
    objective: '',
    tone: '',
    senderName: '',
    openingTemplateName: '',
    openingTemplateLanguage: 'it',
    useDirectSend: false,
    openingMessage: '',
    collectFields: [],
    contextFields: [],
    allowedOutcomes: [],
    handoffWindowMinutes: 45,
    maxTurns: 20,
    model: DEFAULT_MODEL,
    bookliTaskWebhookUrl: '',
    ...editingAiAgent,
    followUpDelays: formatFollowUpDelays(editingAiAgent.followUp?.delayMinutes),
  };

  const handleSubmit = (values) => {
    // id, createdAt, updatedAt and companyId come from the document being
    // edited and must NOT be rewritten: createdAt and updatedAt are
    // client-side Dates and would overwrite the serverTimestamps the saga
    // manages.
    const {
      followUpDelays,
      id,
      createdAt,
      updatedAt,
      companyId,
      ...aiAgentValues
    } = values;
    const delayMinutes = parseFollowUpDelays(followUpDelays);

    const aiAgent = {
      ...aiAgentValues,
      collectFields: Array.isArray(values.collectFields)
        ? values.collectFields
        : [],
      // Exactly these two properties: they are the contract with the engine,
      // which reads them in snapshotAgentConfig.
      contextFields: Array.isArray(values.contextFields)
        ? values.contextFields.map(({ leadFieldId, description }) => ({
            leadFieldId,
            description: (description || '').trim(),
          }))
        : [],
      allowedOutcomes: Array.isArray(values.allowedOutcomes)
        ? values.allowedOutcomes
        : [],
      handoffWindowMinutes: Number(values.handoffWindowMinutes),
      maxTurns: Number(values.maxTurns),
      followUp: { attempts: delayMinutes.length, delayMinutes },
    };

    dispatch(
      props.create
        ? aiAgentsActions.createAiAgent(aiAgent)
        : aiAgentsActions.updateAiAgent({ id: editingAiAgent.id, ...aiAgent }),
    );
    props.closeModal();
  };

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={initialValues}
          validationSchema={formValidation}
          onSubmit={handleSubmit}
        >
          {({ isValid, values, errors }) => (
            <>
              <Row>
                <Col md="5">
                  <Input type="text" name="name" label="Name" />
                </Col>
                <Col md="4">
                  <Select
                    name="channel"
                    id="channel"
                    label="Channel"
                    isMulti={false}
                    options={aiChannels}
                    readOnly={!props.create}
                    subText={t(
                      'Cannot be changed after creation: an agent belongs to its channel. Create a new agent for a different channel.',
                    )}
                  />
                </Col>
                <Col md="3">
                  <Switch
                    name="active"
                    label={t('Active?')}
                    defaultChecked={values.active}
                    subText={t(
                      'Only controls whether new leads are routed to this agent. Conversations already open run to their end: this is not an emergency stop.',
                    )}
                  />
                </Col>
              </Row>
              {!props.create && (
                <Row>
                  <Col md="12">
                    <small className="font-size-12 text-muted">
                      {t(
                        'Changes apply to the conversations that start from now on. The ones already open finish with the configuration they were born with.',
                      )}
                    </small>
                  </Col>
                </Row>
              )}

              <hr />
              <h5 className="font-size-14 mb-3">{t('Behaviour')}</h5>
              <Row>
                <Col md="12">
                  <Input
                    type="textarea"
                    name="objective"
                    label="Objective"
                    rows="5"
                    subText={t(
                      'Describe in plain language what the agent must achieve talking to the lead.',
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <Input
                    type="textarea"
                    name="tone"
                    label="Tone"
                    rows="3"
                    subText={t(
                      'How the agent should sound and what it must never say. Leave empty for a neutral, professional tone.',
                    )}
                  />
                </Col>
              </Row>

              <Collapse isOpen={values.channel === 'whatsapp'}>
                <Row>
                  <Col md="5">
                    <Input
                      type="text"
                      name="senderName"
                      label="Sender name"
                      subText={t(
                        'The name the agent gives the lead. It fills the second variable of the opening template, and the agent keeps using it for the rest of the conversation.',
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <Input
                      type="text"
                      name="openingTemplateName"
                      label="Opening template"
                      subText={t(
                        'Name of the Meta-approved template used to open the conversation. WhatsApp forbids free-form first messages.',
                      )}
                    />
                  </Col>
                  <Col md="3">
                    <Input
                      type="text"
                      name="openingTemplateLanguage"
                      label="Template language"
                    />
                  </Col>
                  <Col md="4">
                    <Switch
                      name="useDirectSend"
                      label={t('Use Meta Direct Send?')}
                      defaultChecked={values.useDirectSend}
                      subText={t(
                        'Opens the conversation without a pre-approved template. Only for WhatsApp accounts Meta has enabled for it, and only for utility messages.',
                      )}
                    />
                  </Col>
                </Row>
                <Collapse isOpen={values.useDirectSend}>
                  <Row>
                    <Col md="11">
                      <Input
                        type="textarea"
                        name="openingMessage"
                        label="Opening message"
                        rows="3"
                        subText={t(
                          'The first message the lead receives. Utility content only: no offers or promotions, or Meta refuses the send.',
                        )}
                      />
                    </Col>
                  </Row>
                </Collapse>
              </Collapse>

              <Collapse isOpen={values.channel === 'call'}>
                <Row>
                  <Col md="9">
                    <Input
                      type="text"
                      name="bookliTaskWebhookUrl"
                      label="Bookli task webhook URL"
                    />
                  </Col>
                </Row>
              </Collapse>

              <hr />
              <h5 className="font-size-14 mb-3">{t('Data to collect')}</h5>
              <Row>
                <Col md="12">
                  <FieldArray
                    name="collectFields"
                    render={(arrayHelpers) => {
                      const fields = values.collectFields;
                      return (
                        <FormGroup>
                          <Label htmlFor="collectFields">
                            {t('Lead fields the agent may write')}
                          </Label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline btn-light ml-4"
                            onClick={() =>
                              arrayHelpers.push({
                                leadFieldId: '',
                                required: false,
                                overwrite: false,
                                hint: '',
                              })
                            }
                          >
                            <i className="bx bx-plus font-size-11 align-middle"></i>{' '}
                            {t('Add')}
                          </button>
                          {Array.isArray(fields) &&
                            fields.map((field, index) => (
                              <Row key={index} className="mb-2">
                                <Col md="4">
                                  <Select
                                    name={`collectFields.${index}.leadFieldId`}
                                    label="Field"
                                    isMulti={false}
                                    isSearchable
                                    addonType="append"
                                    options={leadFields.map(({ id, name }) => ({
                                      label: name,
                                      value: id,
                                    }))}
                                  >
                                    <button
                                      className="btn btn-danger p-0"
                                      style={{ width: '31px' }}
                                      type="button"
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      <i className="bx bx-trash d-block font-size-15" />
                                    </button>
                                  </Select>
                                </Col>
                                <Col md="4">
                                  <Input
                                    type="text"
                                    name={`collectFields.${index}.hint`}
                                    label="Hint"
                                    subText={t(
                                      'Shown to the agent next to the field, both when asking for it and once it is known. Describe what the value means, not only how to ask for it.',
                                    )}
                                  />
                                </Col>
                                <Col md="2">
                                  <Switch
                                    name={`collectFields.${index}.required`}
                                    label={t('Required?')}
                                    defaultChecked={fields[index]?.required}
                                  />
                                </Col>
                                <Col md="2">
                                  <Switch
                                    name={`collectFields.${index}.overwrite`}
                                    label={t('Overwrite?')}
                                    defaultChecked={fields[index]?.overwrite}
                                  />
                                </Col>
                              </Row>
                            ))}
                          <small className="font-size-12 text-muted">
                            {t(
                              'Overwrite lets the agent replace a value the lead already has. Off by default.',
                            )}
                          </small>
                          <ListError error={errors.collectFields} />
                        </FormGroup>
                      );
                    }}
                  />
                </Col>
              </Row>

              <hr />
              <h5 className="font-size-14 mb-3">
                {t('Data the agent should know')}
              </h5>
              <Row>
                <Col md="12">
                  <FieldArray
                    name="contextFields"
                    render={(arrayHelpers) => {
                      const fields = values.contextFields;
                      return (
                        <FormGroup>
                          <Label htmlFor="contextFields">
                            {t('Lead fields the agent can read')}
                          </Label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline btn-light ml-4"
                            onClick={() =>
                              arrayHelpers.push({
                                leadFieldId: '',
                                description: '',
                              })
                            }
                          >
                            <i className="bx bx-plus font-size-11 align-middle"></i>{' '}
                            {t('Add')}
                          </button>
                          {Array.isArray(fields) &&
                            fields.map((field, index) => (
                              <Row key={index} className="mb-2">
                                <Col md="4">
                                  <Select
                                    name={`contextFields.${index}.leadFieldId`}
                                    label="Field"
                                    isMulti={false}
                                    isSearchable
                                    addonType="append"
                                    options={leadFields.map(({ id, name }) => ({
                                      label: name,
                                      value: id,
                                    }))}
                                  >
                                    <button
                                      className="btn btn-danger p-0"
                                      style={{ width: '31px' }}
                                      type="button"
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      <i className="bx bx-trash d-block font-size-15" />
                                    </button>
                                  </Select>
                                </Col>
                                <Col md="8">
                                  <Input
                                    type="text"
                                    name={`contextFields.${index}.description`}
                                    label="Description"
                                    subText={t(
                                      'What this value means for this agent, for example the car brand the person wants to test drive. The agent sees this text and the value, never the field name.',
                                    )}
                                  />
                                </Col>
                              </Row>
                            ))}
                          <small className="font-size-12 text-muted">
                            {t(
                              'The agent reads these fields and never writes them. A field cannot be both collected and read.',
                            )}
                          </small>
                          <ListError error={errors.contextFields} />
                        </FormGroup>
                      );
                    }}
                  />
                </Col>
              </Row>

              <hr />
              <h5 className="font-size-14 mb-3">{t('Allowed outcomes')}</h5>
              <Row>
                <Col md="8">
                  <Select
                    name="allowedOutcomes"
                    id="allowedOutcomes"
                    label="Outcomes"
                    isMulti
                    options={aiOutcomes}
                    subText={t(
                      'Monetization orders come from the acquisition order, not from the agent.',
                    )}
                  />
                </Col>
              </Row>

              <hr />
              <h5 className="font-size-14 mb-3">{t('Timing and engine')}</h5>
              <Row>
                <Col md="3">
                  <Input
                    type="number"
                    name="handoffWindowMinutes"
                    label="Handoff window (minutes)"
                    subText={t(
                      'Counted from the start of the conversation. Once it expires without an outcome, the lead goes to the call center queue.',
                    )}
                  />
                </Col>
                <Col md="3">
                  <Input
                    type="text"
                    name="followUpDelays"
                    label="Follow-up delays"
                    subText={t(
                      'Minutes of silence after which the agent nudges the lead. The clock restarts every time she replies, and the number of values is also the maximum number of nudges for the whole conversation. Each one must be shorter than the handoff window, or it can never fire.',
                    )}
                  />
                </Col>
                <Col md="2">
                  <Input type="number" name="maxTurns" label="Max turns" />
                </Col>
                <Col md="4">
                  <Select
                    name="model"
                    id="model"
                    label="Model"
                    isMulti={false}
                    options={aiModels}
                  />
                </Col>
              </Row>

              <div className="modal-footer">
                <SubmitButton
                  disabled={!isValid || props.loading}
                  create={props.create}
                />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormAiAgent;
